<template>
  <div id="s-intro" class="screen active">
    <div class="brand-bar">
      <img src="@/assets/images/Logo_TestVocacional.png" alt="Test Vocacional Logo">
      <div class="brand-tag">Test de Vocación</div>
    </div>

    <div class="card">
      <div class="intro-eyebrow">Construye tu futuro</div>
      <h1 class="intro-h">Descubre tu <span>perfil vocacional</span> ideal</h1>
      <p class="intro-sub">Completa este breve test diseñado con inteligencia artificial para encontrar las carreras que mejor se adaptan a tus fortalezas, intereses y el mercado laboral del futuro.</p>

      <div class="pills">
        <div class="pill">⏱ 3 minutos</div>
        <div class="pill">🧠 IA Potenciada</div>
        <div class="pill accent">✨ 100% Personalizado</div>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Nombres *</label>
          <input 
            type="text" 
            v-model="localData.name" 
            @input="onNameInput"
            :class="{ err: showError && errors.name }" 
            placeholder="Ej. Juan" 
          />
          <span class="hint" v-if="showError && errors.name">{{ errors.name }}</span>
        </div>
        <div class="field">
          <label>Apellidos *</label>
          <input 
            type="text" 
            v-model="localData.apellidos" 
            @input="onApellidosInput"
            :class="{ err: showError && errors.apellidos }" 
            placeholder="Ej. Pérez" 
          />
          <span class="hint" v-if="showError && errors.apellidos">{{ errors.apellidos }}</span>
        </div>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Correo Electrónico *</label>
          <input 
            type="email" 
            v-model="localData.email" 
            :class="{ err: (showError && errors.email) || isEmailInvalid }" 
            placeholder="juan@correo.com" 
          />
          <span class="hint" v-if="isEmailInvalid">Ingresa un correo electrónico válido (ejemplo@dominio.com).</span>
          <span class="hint" v-else-if="showError && errors.email">{{ errors.email }}</span>
        </div>
        <div class="field">
          <label>Celular *</label>
          <input 
            type="tel" 
            v-model="localData.celular" 
            @input="onCelularInput"
            :class="{ err: showError && errors.celular }" 
            placeholder="3000000000" 
            maxlength="10" 
          />
          <span class="hint" v-if="showError && errors.celular">{{ errors.celular }}</span>
        </div>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Tipo de Documento *</label>
          <select 
            v-model="localData.tipo_documento" 
            :class="{ err: showError && errors.tipo_documento }"
            @change="validateDocumento"
          >
            <option value="" disabled selected>Selecciona...</option>
            <option value="CC">Cédula de Ciudadanía</option>
            <option value="TI">Tarjeta de Identidad</option>
            <option value="CE">Cédula de Extranjería</option>
            <option value="PPT">Permiso por Protección Temporal (PPT)</option>
          </select>
          <span class="hint" v-if="showError && errors.tipo_documento">{{ errors.tipo_documento }}</span>
        </div>
        <div class="field">
          <label>Número de Documento *</label>
          <input 
            type="text" 
            v-model="localData.numero_documento" 
            @input="onDocumentoInput"
            :class="{ err: showError && errors.numero_documento }" 
            placeholder="Número" 
            maxlength="20" 
          />
          <span class="hint" v-if="showError && errors.numero_documento">{{ errors.numero_documento }}</span>
        </div>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Género *</label>
          <select v-model="localData.genero" :class="{ err: showError && errors.genero }">
            <option value="" disabled selected>Selecciona...</option>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
            <option value="O">Otro</option>
          </select>
          <span class="hint" v-if="showError && errors.genero">{{ errors.genero }}</span>
        </div>
        <div class="field">
          <label>Ciudad *</label>
          <input 
            type="text" 
            v-model="localData.ciudad" 
            @blur="onCityBlur"
            :class="{ err: showError && errors.ciudad }" 
            placeholder="Ej: Bogotá" 
            list="ciudades-colombia" 
          />
          <datalist id="ciudades-colombia">
            <option v-for="(c, index) in citiesList" :key="c.id || index" :value="c.value"></option>
          </datalist>
          <span class="hint" v-if="showError && errors.ciudad">{{ errors.ciudad }}</span>
        </div>
      </div>

      <div class="field-grid">
        <div class="field">
          <label>Nombre del Colegio *</label>
          <input 
            type="text" 
            v-model="localData.colegio" 
            :disabled="!isCityValid"
            :class="{ err: (showError && errors.colegio) || (localData.ciudad && !isCityValid) }" 
            placeholder="Nombre de tu colegio" 
            list="colegios-list"
          />
          <datalist id="colegios-list">
            <option v-for="(s, index) in schoolsList" :key="s.id || index" :value="s.value"></option>
          </datalist>
          <span class="hint warning-hint" v-if="!localData.ciudad">Primero debes ingresar una ciudad para habilitar este campo.</span>
          <span class="hint warning-hint" v-else-if="!isCityValid">Selecciona una ciudad válida de la lista para ingresar el colegio.</span>
          <span class="hint" v-else-if="showError && errors.colegio">{{ errors.colegio }}</span>
          <span class="hint info-hint" v-else-if="localData.colegio && localData.colegio.trim().length < 3">Ingresa al menos 3 letras para ver sugerencias.</span>
        </div>
        <div class="field">
          <label>Grado Actual / Último cursado *</label>
          <select v-model="localData.grado" :class="{ err: showError && errors.grado }">
            <option value="" disabled selected>Selecciona...</option>
            <option value="10">Décimo</option>
            <option value="11">Once</option>
            <option value="graduado">Ya soy bachiller</option>
            <option value="profesional">Ya soy profesional</option>
          </select>
          <span class="hint" v-if="showError && errors.grado">{{ errors.grado }}</span>
        </div>
      </div>

      <div :class="['consent-box', { err: showError && errors.terms }]">
        <div class="consent-row">
          <input type="checkbox" id="terms" class="consent-check" v-model="localData.terms">
          <label for="terms" class="consent-text">
            Acepto el <a href="https://ibero.edu.co/tratamiento-uso-datos-personales" target="_blank">tratamiento de mis datos personales</a> para el envío de los resultados de este test y recibir información sobre programas académicos de la IBERO que se ajusten a mi perfil. <strong>Tus datos están seguros con nosotros.</strong>
          </label>
        </div>
        <div class="consent-err-msg">Debes aceptar el tratamiento de datos para continuar.</div>
      </div>

      <div class="cta-row">
        <button class="btn-primary" @click="startTest">Iniciar Test Ahora</button>
      </div>

      <div class="global-err-msg" v-if="showError && hasErrors">
        Por favor corrija o completa los campos marcados en rojo antes de continuar.
      </div>
    </div>
  </div>
</template>

<script>
import IberoHelpers from '@/store/helpers.js';

export default {
  name: 'TestIntro',
  props: {
    userData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localData: { ...this.userData },
      showError: false,
      ciudadesPrincipales: [
        'Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Cúcuta', 'Bucaramanga',
        'Ibagué', 'Soledad', 'Pereira', 'Santa Marta', 'Soacha', 'Pasto', 'Montería',
        'Villavicencio', 'Manizales', 'Bello', 'Neiva', 'Valledupar', 'Armenia', 'Buenaventura',
        'Popayán', 'Sincelejo', 'Palmira', 'Floridablanca', 'Riohacha', 'Envigado', 'Tuluá',
        'Dosquebradas', 'San Andrés', 'Tunja', 'Girón', 'Florencia', 'Yopal', 'Quibdó', 'Mocoa'
      ]
    };
  },
  created() {
    IberoHelpers.getCitiesList(this.$store);
  },
  computed: {
    citiesList() {
      const query = this.localData.ciudad ? this.localData.ciudad.trim() : '';
      if (!query) {
        return [];
      }

      const rawList = (this.$store.state.CitiesList && this.$store.state.CitiesList.length > 0)
        ? this.$store.state.CitiesList
        : this.ciudadesPrincipales.map(c => ({ id: c, value: c }));

      const normalize = str => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const normQuery = normalize(query);

      return rawList
        .filter(c => c && c.value && normalize(c.value).includes(normQuery))
        .slice(0, 3);
    },
    selectedCityObj() {
      const cityName = (this.localData.ciudad || '').trim();
      if (!cityName) return null;
      const list = (this.$store.state.CitiesList && this.$store.state.CitiesList.length > 0)
        ? this.$store.state.CitiesList
        : this.ciudadesPrincipales.map(c => ({ id: c, value: c }));

      const normalize = str => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      const cleanPunct = str => normalize(str).replace(/[.,\-–—_]/g, ' ').replace(/\s+/g, ' ').trim();

      const normName = normalize(cityName);
      const cleanName = cleanPunct(cityName);

      // 1. Coincidencia exacta normalizada
      const exact = list.find(c => c && normalize(c.value) === normName);
      if (exact) return exact;

      // 2. Coincidencia sin signos de puntuación / espacios múltiples
      const punctMatch = list.find(c => c && cleanPunct(c.value) === cleanName);
      if (punctMatch) return punctMatch;

      // 3. Mapeo de alias comunes (por ej. Bogotá -> Bogotá D. C., Cali -> Santiago de Cali, etc.)
      const aliasMap = {
        'bogota': ['bogota d. c.', 'bogota d.c.', 'bogota dc', 'bogota d c', 'bogota'],
        'cali': ['santiago de cali', 'cali'],
        'cartagena': ['cartagena de indias', 'cartagena'],
        'cucuta': ['san jose de cucuta', 'cucuta'],
        'santa marta': ['distrito turistico cultural e historico de santa marta', 'santa marta']
      };

      for (const [aliasKey, targetNames] of Object.entries(aliasMap)) {
        if (cleanName === aliasKey || targetNames.includes(cleanName)) {
          const aliasFound = list.find(c => {
            if (!c || !c.value) return false;
            const cNorm = normalize(c.value);
            const cClean = cleanPunct(c.value);
            return targetNames.some(t => cNorm === t || cClean === t || cClean.startsWith(t));
          });
          if (aliasFound) return aliasFound;
        }
      }

      // 4. Si la ciudad en la lista empieza con lo escrito o viceversa (mínimo 4 letras)
      if (cleanName.length >= 4) {
        const prefixMatch = list.find(c => c && c.value && (cleanPunct(c.value).startsWith(cleanName) || cleanName.startsWith(cleanPunct(c.value))));
        if (prefixMatch) return prefixMatch;
      }

      return null;
    },
    isCityValid() {
      return !!this.selectedCityObj;
    },
    schoolsList() {
      const query = this.localData.colegio ? this.localData.colegio.trim() : '';
      if (!query || query.length < 3) {
        return [];
      }

      const rawList = this.$store.state.SchoolList || [];
      const normalize = str => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const normQuery = normalize(query);

      return rawList
        .filter(s => s && s.value && normalize(s.value).includes(normQuery))
        .slice(0, 3);
    },
    isEmailInvalid() {
      const email = (this.localData.email || '').trim();
      if (!email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(email);
    },
    errors() {
      const errs = {};

      // Validar Nombres
      if (!this.localData.name || !this.localData.name.trim()) {
        errs.name = 'El nombre es obligatorio.';
      }

      // Validar Apellidos
      if (!this.localData.apellidos || !this.localData.apellidos.trim()) {
        errs.apellidos = 'El apellido es obligatorio.';
      }

      // Validar Email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.localData.email) {
        errs.email = 'El correo electrónico es obligatorio.';
      } else if (!emailRegex.test(this.localData.email)) {
        errs.email = 'Ingresa un correo electrónico válido.';
      }

      // Validar Celular (solo números, entre 7 y 10 dígitos)
      if (!this.localData.celular) {
        errs.celular = 'El celular es obligatorio.';
      } else if (this.localData.celular.length < 7 || this.localData.celular.length > 10) {
        errs.celular = 'Debe tener entre 7 y 10 números.';
      }

      // Validar Tipo Documento
      if (!this.localData.tipo_documento) {
        errs.tipo_documento = 'Selecciona el tipo de documento.';
      }

      // Validar Número Documento según Tipo
      const doc = this.localData.numero_documento ? this.localData.numero_documento.trim() : '';
      if (!doc) {
        errs.numero_documento = 'El número de documento es obligatorio.';
      } else if (this.localData.tipo_documento === 'CC') {
        if (!/^\d+$/.test(doc)) {
          errs.numero_documento = 'Para Cédula de Ciudadanía solo se permiten números.';
        } else if (doc.length < 7) {
          errs.numero_documento = 'Para Cédula de Ciudadanía se requieren mínimo 7 números.';
        }
      } else if (this.localData.tipo_documento === 'TI') {
        if (!/^\d+$/.test(doc)) {
          errs.numero_documento = 'Para Tarjeta de Identidad solo se permiten números.';
        } else if (doc.length < 10) {
          errs.numero_documento = 'Para Tarjeta de Identidad se requieren mínimo 10 números.';
        }
      } else if (this.localData.tipo_documento === 'CE') {
        if (doc.length < 5) {
          errs.numero_documento = 'Para Cédula de Extranjería se requieren mínimo 5 caracteres.';
        }
      } else if (this.localData.tipo_documento === 'PPT') {
        if (!/^[a-zA-Z0-9]+$/.test(doc)) {
          errs.numero_documento = 'Para PPT solo se permiten letras y números.';
        } else if (doc.length < 5) {
          errs.numero_documento = 'Para Permiso por Protección Temporal se requieren mínimo 5 caracteres.';
        }
      }

      // Validar Género
      if (!this.localData.genero) {
        errs.genero = 'Selecciona el género.';
      }

      // Validar Ciudad
      if (!this.localData.ciudad || !this.localData.ciudad.trim()) {
        errs.ciudad = 'La ciudad es obligatoria.';
      } else if (!this.isCityValid) {
        errs.ciudad = 'Selecciona una ciudad válida de la lista.';
      }

      // Validar Colegio
      const colegioVal = this.localData.colegio ? this.localData.colegio.trim() : '';
      if (!colegioVal) {
        errs.colegio = 'El nombre del colegio es obligatorio.';
      } else if (colegioVal.length < 3) {
        errs.colegio = 'El nombre del colegio debe tener al menos 3 caracteres.';
      }

      // Validar Grado
      if (!this.localData.grado) {
        errs.grado = 'Selecciona el grado actual.';
      }

      // Validar Términos
      if (!this.localData.terms) {
        errs.terms = 'Debes aceptar los términos y condiciones.';
      }

      return errs;
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    }
  },
  watch: {
    selectedCityObj: {
      immediate: true,
      handler(newCity, oldCity) {
        const newId = newCity ? newCity.id : null;
        const oldId = oldCity ? oldCity.id : null;
        if (newId !== oldId) {
          if (newId) {
            IberoHelpers.getSchoolsByCity(this.$store, newId);
          } else {
            this.localData.colegio = '';
            if (this.$store.state.SchoolList) {
              this.$store.state.SchoolList.length = 0;
            }
          }
        }
      }
    }
  },
  methods: {
    onNameInput() {
      if (this.localData.name) {
        // Remover números y caracteres especiales (permitir letras latinas, tildes, ñ y espacios)
        this.localData.name = this.localData.name.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
      }
    },
    onApellidosInput() {
      if (this.localData.apellidos) {
        // Remover números y caracteres especiales
        this.localData.apellidos = this.localData.apellidos.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
      }
    },
    onCelularInput() {
      if (this.localData.celular) {
        // Remover todo lo que no sea dígito
        this.localData.celular = this.localData.celular.replace(/\D/g, '');
      }
    },
    onDocumentoInput() {
      if (!this.localData.numero_documento) return;
      if (this.localData.tipo_documento === 'CC' || this.localData.tipo_documento === 'TI') {
        // Solo dígitos para CC y TI
        this.localData.numero_documento = this.localData.numero_documento.replace(/\D/g, '');
      } else if (this.localData.tipo_documento === 'CE' || this.localData.tipo_documento === 'PPT') {
        // Permitir letras y números para CE y PPT
        this.localData.numero_documento = this.localData.numero_documento.replace(/[^a-zA-Z0-9]/g, '');
      }
    },
    onCityBlur() {
      if (this.selectedCityObj && this.localData.ciudad !== this.selectedCityObj.value) {
        this.localData.ciudad = this.selectedCityObj.value;
      }
    },
    validateDocumento() {
      this.onDocumentoInput();
    },
    startTest() {
      if (this.selectedCityObj && this.localData.ciudad !== this.selectedCityObj.value) {
        this.localData.ciudad = this.selectedCityObj.value;
      }
      if (this.hasErrors) {
        this.showError = true;
        return;
      }
      this.showError = false;
      this.$emit('update-user', this.localData);
      this.$emit('next-step', 'TestQuestions');
    }
  }
};
</script>

<style scoped>
.field input.err, .field select.err {
  border-color: #E24B4A !important;
  background-color: #FEF4F4;
}

.hint {
  font-size: 11px;
  color: #A32D2D;
  margin-top: 3px;
  display: block;
}

.hint.warning-hint {
  color: #D97706 !important;
}

.hint.info-hint {
  color: #6B7280 !important;
}

.field input:disabled {
  background-color: #F3F4F6 !important;
  cursor: not-allowed;
  opacity: 0.7;
}

.global-err-msg {
  font-size: 12px;
  color: #A32D2D;
  margin-top: 1rem;
  font-weight: 600;
  display: block;
}
</style>

